// Copyright (c) 2019, 2020 by Michael Marucheck
//
// Use of this source code is governed by an MIT-style license that
// can be found in the LICENSE file distributed with this file.

import uuidv4 from "uuid/v4";
import { logCrash } from "./store/crash";

export const UID_NEW = "new";

export function resolveUid(uid: string): string {
  if (uid === UID_NEW) {
    return uuidv4();
  } else if (isUID(uid)) {
    return uid;
  } else {
    // TODO 404
    throw new Error(`Unrecognized UID '${uid}'`);
  }
}

export const DATE_REGEX = /[0-9]{4}-[0-9]{2}-[0-9]{2}/;

export function isDate(date: string): boolean {
  return DATE_REGEX.test(date);
}

export function todayString() {
  const twoDigits = (n: number) => n.toString().padStart(2, "0");
  const now = new Date();
  const year = now.getFullYear();
  const month = twoDigits(now.getMonth() + 1);
  const day = twoDigits(now.getDate());
  return `${year}-${month}-${day}`;
}

export interface HrMin {
  hours: string;
  minutes: string;
}

export function hrMinFromSeconds(durationInSec: number): HrMin {
  const h = Math.floor(durationInSec / 3600);
  const m = Math.floor((durationInSec - h * 3600) / 60);
  const hours = h.toString();
  const minutes = m.toString().padStart(2, "0");
  return { hours, minutes };
}

export function hrMinSeconds(hrmin: HrMin): number {
  return throwIfNull(tryHrMinSeconds(hrmin));
}

export function isHrMin(hrmin: HrMin): boolean {
  return tryHrMinSeconds(hrmin) != null;
}

export function tryHrMinSeconds(hrmin: HrMin): number | null {
  const { hours, minutes } = hrmin;
  if ((hours == null || hours === "") && (minutes == null || minutes === "")) {
    return null;
  }

  const allDigits = /^[0-9]*$/;
  if (!allDigits.test(hours) || !allDigits.test(minutes)) {
    return null;
  }

  const h = hours == null || hours === "" ? 0 : Number.parseInt(hours);
  const m = minutes == null || minutes === "" ? 0 : Number.parseInt(minutes);
  return h * 3600 + m * 60;
}

export function durationString(durationInSec: number): string {
  const hours = Math.floor(durationInSec / 3600);
  const minutes = Math.floor((durationInSec - hours * 3600) / 60);
  const hh = hours.toString();
  const mm = minutes.toString().padStart(2, "0");
  return `${hh}:${mm}`;
}

export function isDuration(duration: string): boolean {
  return tryDurationSeconds(duration) !== null;
}

export function durationSeconds(duration: string): number {
  return throwIfNull(tryDurationSeconds(duration));
}

export function tryDurationSeconds(duration: string): number | null {
  const hours = (x: string) => Math.round(Number.parseFloat(x) * 60 * 60);
  const minutes = (x: string) => Number.parseInt(x) * 60;

  if (duration == null || duration === "") {
    return null;
  }
  if (/^[0-9]+:[0-9][0-9]$/.test(duration)) {
    const [h, m] = duration.split(":");
    return hours(h) + minutes(m);
  }
  if (/^[0-9.]+ ?h$/.test(duration)) {
    return hours(duration.substring(0, duration.length - 1).trim());
  }
  if (/^[0-9.]+ ?hours?$/.test(duration)) {
    const split = duration.indexOf("hour");
    return hours(duration.substring(0, split).trim());
  }
  if (/^[0-9.]+$/.test(duration)) {
    return hours(duration);
  }
  return null;
}

// TODO: for now we only allow U.S. phone numbers
/* const PHONE_REGEX = /([-+.() ]*[0-9]){10,}/ */
const PHONE_REGEX = /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/;

export function isPhone(phone: string): boolean {
  return PHONE_REGEX.test(phone);
}

export const UUID_REGEX = /[0-9a-zA-Z]{8}-[0-9a-zA-Z]{4}-[0-9a-zA-Z]{4}-[0-9a-zA-Z]{4}-[0-9a-zA-Z]{12}/;

export function isUUID(uuid: string): boolean {
  return UUID_REGEX.test(uuid);
}

export const FIREBASE_UID_REGEX = /[0-9a-zA-Z]*/;

export function isFirebaseUID(uid: string): boolean {
  return FIREBASE_UID_REGEX.test(uid);
}

export function isUID(uid: string): boolean {
  return isFirebaseUID(uid) || isUUID(uid);
}

export const CLASS_YEAR_REGEX = /[0-9]{4}/;

export function isClassYearFormat(year: string): boolean {
  return CLASS_YEAR_REGEX.test(year);
}

export function isClassYear(year: string): boolean {
  if (!CLASS_YEAR_REGEX.test(year)) {
    return false;
  }
  const parsed = Number.parseInt(year);
  const now = new Date();
  const nowYear = now.getFullYear();
  return parsed >= nowYear - 5 && parsed <= nowYear + 5;
}

export function classYearString(year: number): string {
  if (Number.isNaN(year)) {
    return "";
  } else {
    return year.toString();
  }
}

export function classYearNumber(year: string): number {
  return Number.parseInt(year);
}

export type Comparer<T> = (a: T, b: T) => number;
export type FieldGetter<Obj, Field> = (o: Obj) => Field;

export function comparerByField<Obj, Field>(
  get: FieldGetter<Obj, Field>,
  reverse: boolean = false
): Comparer<Obj> {
  return (aObj: Obj, bObj: Obj) => {
    const a = get(aObj);
    const b = get(bObj);
    if (a < b) return reverse ? 1 : -1;
    if (a > b) return reverse ? -1 : 1;
    return 0;
  };
}

export function sortBy<Obj, Field>(
  array: Obj[],
  get: FieldGetter<Obj, Field>
): Obj[] {
  return [...array].sort(comparerByField(get));
}

export function revSortBy<Obj, Field>(
  array: Obj[],
  get: FieldGetter<Obj, Field>
): Obj[] {
  return [...array].sort(comparerByField(get, true));
}

export function mostCommon(array: string[]): string | null {
  const counts = count(array);
  const keys = Object.keys(counts);

  if (keys.length === 0) {
    return null;
  } else {
    return Object.keys(counts).reduce((acc, k) =>
      counts[acc] > counts[k] ? acc : k
    );
  }

  type Counts = { [key: string]: number };

  function count(array: string[]): { [key: string]: number } {
    return array.reduce((acc, x) => incr(acc, x), {} as Counts);
  }

  function incr(acc: Counts, key: string): Counts {
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }
}

export function capitalizeFirstLetter(s: string): string {
  return s[0].toUpperCase() + s.substring(1);
}

export function orNull<T>(call: () => T): T | null {
  try {
    return call();
  } catch (err) {
    return null;
  }
}

export async function orNullA<T>(call: () => Promise<T>): Promise<T | null> {
  try {
    return await call();
  } catch (err) {
    return null;
  }
}

export function throwIfNull<T>(
  item: T | null | undefined,
  message?: string
): T {
  if (item === null) {
    throw new Error(message || "Unexpected null value");
  }
  if (item == null) {
    throw new Error(message || "Unexpected undefined value");
  }
  return item;
}

export function expectOne<T>(array: T[], noun: string, criterion: string): T {
  if (array.length !== 1) {
    throw new Error(
      `Expected one ${noun} to match ${criterion}, got ${array.length}`
    );
  }
  return array[0];
}

export function lazy<T>(init: () => T): () => T {
  let called = false;
  let value: T;

  return () => {
    if (!called) {
      called = true;
      value = init();
    }
    return value;
  };
}

export type Action = () => void;

export function debounce(action: Action, delayMs: number = 100): Action {
  let timer: NodeJS.Timeout | null = null;
  return () => {
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    } else {
    }
    timer = setTimeout(action, delayMs);
  };
}

export function ignore<T>(x: T): void {}

export type CancelFunc = () => void;

export class CancelError extends Error {
  readonly isCancelError?: boolean;

  constructor(message: string = "Operation canceled") {
    super(message);
    // https://stackoverflow.com/a/48342359
    const proto = new.target.prototype;
    if (Object.setPrototypeOf) {
      Object.setPrototypeOf(this, proto);
    } else {
      (this as any).__proto__ = proto;
    }

    this.isCancelError = true;
  }
}

export function isCancelError(error: Error): boolean {
  return !!(error as any).isCancelError;
}

export type BusySetter = (x: boolean) => any;

export function logIfError<T>(call: () => T): T | null {
  return logAndTrack(() => {}, call);
}
export function logIfErrorA<T>(promise: () => Promise<T>): Promise<T | null> {
  return logAndTrackA(() => {}, promise);
}

export function logAndTrack<T>(busy: BusySetter, call: () => T): T | null {
  try {
    busy(true);
    const result = call();
    busy(false);
    return result;
  } catch (err) {
    if (isCancelError(err)) {
      debug(`Ignoring cancelation: ${err.message}`);
    } else {
      logCrash(err);
    }
    return null;
  }
}

export async function logAndTrackA<T>(
  busy: BusySetter,
  promise: () => Promise<T>
): Promise<T | null> {
  try {
    busy && busy(true);
    const result = await promise();
    busy && busy(false);
    return result;
  } catch (err) {
    if (isCancelError(err)) {
      debug(`Ignoring cancelation: ${err.message}`);
    } else {
      logCrash(err);
    }
    return null;
  }
}

export function fork(start: () => Promise<any>): CancelFunc {
  const token = setImmediate(() => logIfErrorA(start));
  return () => clearImmediate(token);
}

export function lazyFork(start: () => Promise<any>): () => CancelFunc {
  return () => fork(start);
}

export function jsonShortStrings(key: string, value: any) {
  if (typeof value === "string") {
    const n = value.length;
    if (n > 50) {
      return `${value.substring(0, 32)}...${value.substring(n - 16, n)}`;
    }
  }
  return value;
}

export function mapOf<K, V>(array: V[], keyOf: (x: V) => K): Map<K, V> {
  function combine(map: Map<K, V>, x: V): Map<K, V> {
    map.set(keyOf(x), x);
    return map;
  }
  return array.reduce(combine, new Map());
}

export function clearArray<T>(array: T[]): void {
  array.splice(0, array.length);
}

export function randomElement<T>(array: T[]): T {
  if (array.length === 0) {
    throw new Error("Cannot return random element of empty array");
  }
  const index = Math.floor(Math.random() * array.length);
  return array[index];
}

export function firstNonNull<T>(...items: (T | null | undefined)[]): T {
  for (let item of items) {
    if (item != null) {
      return item;
    }
  }
  throw new Error("No non-null item found");
}

export function getenv(key: string): string {
  const value = process.env[key];
  if (value == null) {
    throw fail(`Expected environment variable '${key}' to be set.`);
  }
  return value;
}

export function b64decode(s: string): string {
  return Buffer.from(s, "base64").toString("utf8");
}

export function b64encode(s: string): string {
  return Buffer.from(s, "utf8").toString("base64");
}

export function encouragement(): string {
  return randomElement([
    "Awesome!",
    "Bravo!",
    "Congratulations!",
    "Cool!",
    "Cool beans!",
    "Couldn't have done that better myself!",
    "Excellent!",
    "Fantastic!",
    "Gee, that was just swell!",
    "Good work!",
    "Great!",
    "Incredible.",
    "Looks good!",
    "Nailed it!",
    "Nice!",
    "Nicely done!",
    "Outstanding!",
    "Perfect!",
    "So much win!",
    "Success!",
    "Sweet!",
    "Terrific!",
    "That was totally wicked! (in a good way)",
    "Very nice!",
    "Well done!",
    "Woo hoo!",
    "You did it!",
    "You made that look so easy.",
    "You're the best!",
  ]);
}

export interface Outs {
  debug(message: string, state?: any): void;
  info(message: string, state?: any): void;
  warn(message: string, state?: any): void;
  error(message: string, state?: any): void;
}

export function createOuts(tag: string, enabled: boolean): Outs {
  function writer(kind: string, out: any) {
    return (message: string, state?: any) => {
      if (enabled) {
        out(`${tag} (${kind}): ${message}`);
        if (state != null) {
          out(state);
        }
      }
    };
  }

  return {
    debug: writer("debug", console.log),
    info: writer("info", console.log),
    warn: writer("WARN", console.log),
    error: writer("ERROR", console.error),
  };
}

const { debug } = createOuts("util", true);
